'use client';

import {articleTestIds} from '@shared/Articles/ArticlePage/ArticleTestIds';
import ArticleFaq from '@shared/Articles/ArticleSlug/ArticleFaq/ArticleFaq';
import ArticleFeaturingBlock from '@shared/Articles/ArticleSlug/ArticleFeaturingBlock/ArticleFeaturingBlock';
import {ArticleInfoBlock} from '@shared/Articles/ArticleSlug/ArticleInfoBlockWrapper/ArticleInfoBlockWrapper';
import ArticleSimilarArticles from '@shared/Articles/ArticleSlug/ArticleSimilarArticles/ArticleSimilarArticles';
import ArticleTableOfContents from '@shared/Articles/ArticleSlug/ArticleTableOfContents/ArticleTableOfContents';
import styles from '@web-landing/app/ru/prostore/prostore.module.scss';

import {ScrollTarget} from '@web-landing/analytics/components/ScrollTarget';
import ProxyImage from '@web-landing/components/ProxyImage/ProxyImage';
import {AppFromBackapi} from '@web-landing/components/SearchPicks/MiniPicks/MiniPicks.types';

import {ArticleType} from '@strapi/types';
import {SimilarArticles} from '@strapi/types/Articles';

import {useArticlesAnalytics} from '@web-landing/analytics/hooks/useArticlesAnalytics';

import {localizeDate} from '@web-landing/utils/localizeDate';
import {generateTableOfContentsForArticle} from '@web-landing/utils/tableOfContents';

import {EVENTS} from '@web-landing/analytics/events';

type Props = {
  article: ArticleType;
  similarArticles: SimilarArticles | null;
  appsForBlocks: Record<number, AppFromBackapi[] | undefined>;
};

export const ProStoreDetail = ({
  article,
  similarArticles,
  appsForBlocks,
}: Props) => {
  const tableOfContents = article.disableTableOfContents
    ? null
    : generateTableOfContentsForArticle({
        ...article,
        similar: similarArticles || undefined,
      });

  const blocksWithInstallButton = article.articleContentBlocks
    .filter(({installRustoreButton}) => !!installRustoreButton)
    .map(({id}) => id);

  const shouldRenderTOC = !!tableOfContents?.entries?.length;
  const blocksScrollDepthOffset = shouldRenderTOC ? 3 : 2;
  const faqScrollDepth =
    blocksScrollDepthOffset + article.articleContentBlocks.length;
  const featuringScrollDepth = faqScrollDepth + (article.faq ? 1 : 0);
  const similarScrollDepth = featuringScrollDepth + (article.featuring ? 1 : 0);
  const {
    featuringCardInArticleClick,
    similarArticleClick,
    downloadRustoreButtonClick,
    downloadAppClick,
    appSelect,
    onAccordionClick,
  } = useArticlesAnalytics();

  return (
    <>
      <ScrollTarget
        eventName={EVENTS.scrollDownscroll}
        scrollDepth="1"
        threshold={0.5}
      >
        <p
          className={styles.date}
          data-testid={articleTestIds.proStoreArticlePageDate}
        >
          {localizeDate(article.date)}
        </p>
        <h1
          className={styles.title}
          data-testid={articleTestIds.proStoreArticlePageTitle}
        >
          {article.cardTitle}
        </h1>
        {article?.shortDescription && (
          <span className={styles.shortDescription}>
            {article.shortDescription}
          </span>
        )}
        {article.cardBackgroundImage && (
          <ProxyImage
            className={styles.articleImage}
            imageData={article.cardBackgroundImage}
            alt={article.cardTitle}
            height={article.cardBackgroundImage.height}
            width={article.cardBackgroundImage.width}
            data-testid={articleTestIds.proStoreArticlePageImage}
          />
        )}
      </ScrollTarget>
      {shouldRenderTOC && (
        <ScrollTarget
          eventName={EVENTS.scrollDownscroll}
          scrollDepth="2"
          threshold={0.5}
        >
          <ArticleTableOfContents
            tableOfContents={tableOfContents}
            title={article.tableOfContentsTitle}
            onAccordionClick={onAccordionClick}
          />
        </ScrollTarget>
      )}
      {article.articleContentBlocks.map((elem, index) => (
        <ScrollTarget
          key={index}
          eventName={EVENTS.scrollDownscroll}
          scrollDepth={`${index + blocksScrollDepthOffset}`}
          threshold={0.5}
        >
          <ArticleInfoBlock
            block={elem}
            titleAnchorId={tableOfContents?.blockIdToAnchor[elem.id]}
            selectionApps={appsForBlocks[elem.id]}
            installButtonIndex={
              elem.installRustoreButton
                ? blocksWithInstallButton.indexOf(elem.id)
                : undefined
            }
            onAppSelect={appSelect}
            onDownloadAppClick={downloadAppClick}
            onDownloadRustoreButtonClick={downloadRustoreButtonClick}
            onFeaturingCardInArticleClick={featuringCardInArticleClick}
          />
        </ScrollTarget>
      ))}
      {article.faq && (
        <ScrollTarget
          eventName={EVENTS.scrollDownscroll}
          scrollDepth={`${faqScrollDepth}`}
          threshold={0.5}
        >
          <ArticleFaq faq={article.faq} />
        </ScrollTarget>
      )}
      {article.featuring && (
        <ScrollTarget
          eventName={EVENTS.scrollDownscroll}
          scrollDepth={`${featuringScrollDepth}`}
          threshold={0.5}
        >
          <ArticleFeaturingBlock featuring={article.featuring} />
        </ScrollTarget>
      )}
      {similarArticles && (
        <ScrollTarget
          eventName={EVENTS.scrollDownscroll}
          scrollDepth={`${similarScrollDepth}`}
          threshold={0.5}
        >
          <ArticleSimilarArticles
            similar={similarArticles}
            articleUrl="prostore"
            onSimilarArticleClick={similarArticleClick}
          />
        </ScrollTarget>
      )}
    </>
  );
};
